import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Users, Shield, Plus, Trash2, Edit, Loader2, X,
  Lock, Mail, User, Search, AlertCircle, CheckCircle
} from 'lucide-react';
import './UserManagement.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserManagement = () => {
  const [tab, setTab] = useState('staff');
  const [staffUsers, setStaffUsers] = useState([]);
  const [artistAccounts, setArtistAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(null);
  const [modalData, setModalData] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  const getToken = () => localStorage.getItem('adminToken');
  const getHeaders = () => ({ 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

  useEffect(() => {
    fetchData();
    const stored = localStorage.getItem('adminUser');
    if (stored) try { setCurrentUser(JSON.parse(stored)); } catch {}
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [staffRes, artistRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/staff-users`, { headers: getHeaders() }),
        fetch(`${API_URL}/api/admin/artist-accounts`, { headers: getHeaders() })
      ]);
      if (staffRes.ok) setStaffUsers(await staffRes.json());
      if (artistRes.ok) setArtistAccounts(await artistRes.json());
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const isSuperAdmin = currentUser?.role === 'super_admin';

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/staff-users`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify(modalData)
      });
      if (res.ok) { toast.success('Staff user created'); fetchData(); setShowModal(null); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed'); } finally { setActionLoading(false); }
  };

  const handleDeleteStaff = async (userId) => {
    if (!window.confirm('Delete this staff user? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/staff-users/${userId}`, {
        method: 'DELETE', headers: getHeaders()
      });
      if (res.ok) { toast.success('User deleted'); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed'); }
  };

  const handleResetPassword = async (userId) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/staff-users/${userId}/password`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ new_password: modalData.new_password })
      });
      if (res.ok) { toast.success('Password reset'); setShowModal(null); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed'); } finally { setActionLoading(false); }
  };

  const handleArtistStatus = async (accountId, status) => {
    if (!window.confirm(`${status === 'suspended' ? 'Deactivate' : 'Reactivate'} this artist?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/artist-accounts/${accountId}/status?status=${status}`, {
        method: 'PUT', headers: getHeaders()
      });
      if (res.ok) { toast.success(`Artist ${status}`); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed'); }
  };

  const filteredStaff = staffUsers.filter(u =>
    u.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredArtists = artistAccounts.filter(a =>
    a.artist_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="user-mgmt"><div className="um-loading"><Loader2 size={24} className="spinner" /> Loading...</div></div>;

  return (
    <div className="user-mgmt" data-testid="user-management">
      <div className="um-header">
        <div>
          <h1>User Management</h1>
          <p>Manage staff accounts and artist access</p>
        </div>
        {isSuperAdmin && tab === 'staff' && (
          <button className="um-add-btn" onClick={() => { setShowModal('create'); setModalData({ email: '', display_name: '', password: '', role: 'staff' }); }} data-testid="add-staff-btn">
            <Plus size={16} /> Add Staff User
          </button>
        )}
      </div>

      <div className="um-tabs">
        <button className={`um-tab${tab === 'staff' ? ' active' : ''}`} onClick={() => setTab('staff')} data-testid="tab-staff">
          <Shield size={16} /> Staff Users ({staffUsers.length})
        </button>
        <button className={`um-tab${tab === 'artists' ? ' active' : ''}`} onClick={() => setTab('artists')} data-testid="tab-artists">
          <Users size={16} /> Artist Accounts ({artistAccounts.length})
        </button>
      </div>

      <div className="um-search">
        <Search size={16} />
        <input type="text" placeholder={`Search ${tab}...`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
      </div>

      {tab === 'staff' ? (
        <div className="um-table">
          <div className="um-table-header">
            <span>Name</span>
            <span>Email</span>
            <span>Role</span>
            <span>Actions</span>
          </div>
          {filteredStaff.map(u => (
            <div key={u.id} className="um-table-row" data-testid={`staff-row-${u.id}`}>
              <span className="um-name">
                {u.display_name || u.username}
                {u.email === 'gerry@anyonemusicrecords.com' && <span className="um-badge primary">Primary</span>}
              </span>
              <span className="um-email">{u.email || u.username}</span>
              <span className={`um-role ${u.role}`}>{u.role === 'super_admin' ? 'Super Admin' : 'Staff'}</span>
              <span className="um-actions">
                {isSuperAdmin && (
                  <>
                    <button className="um-icon-btn" title="Reset Password" onClick={() => { setShowModal('reset-pw'); setModalData({ userId: u.id, userName: u.display_name, new_password: '' }); }} data-testid={`reset-pw-${u.id}`}>
                      <Lock size={14} />
                    </button>
                    {u.email !== 'gerry@anyonemusicrecords.com' && (
                      <button className="um-icon-btn danger" title="Delete" onClick={() => handleDeleteStaff(u.id)} data-testid={`delete-staff-${u.id}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="um-table">
          <div className="um-table-header">
            <span>Artist Name</span>
            <span>Email</span>
            <span>Status</span>
            <span>Actions</span>
          </div>
          {filteredArtists.map(a => (
            <div key={a.id} className="um-table-row" data-testid={`artist-row-${a.id}`}>
              <span className="um-name">{a.artist_name}</span>
              <span className="um-email">{a.email}</span>
              <span className={`um-status ${a.status}`}>{a.status}</span>
              <span className="um-actions">
                {isSuperAdmin && a.status === 'approved' && (
                  <button className="um-action-btn danger" onClick={() => handleArtistStatus(a.id, 'suspended')} data-testid={`deactivate-${a.id}`}>
                    Deactivate
                  </button>
                )}
                {isSuperAdmin && a.status === 'suspended' && (
                  <button className="um-action-btn success" onClick={() => handleArtistStatus(a.id, 'approved')} data-testid={`reactivate-${a.id}`}>
                    Reactivate
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showModal === 'create' && (
        <div className="um-modal-overlay" onClick={() => setShowModal(null)}>
          <div className="um-modal" onClick={e => e.stopPropagation()}>
            <div className="um-modal-header"><h2><Plus size={18} /> New Staff User</h2><button onClick={() => setShowModal(null)}><X size={18} /></button></div>
            <form onSubmit={handleCreateStaff} className="um-modal-form">
              <div className="um-form-group">
                <label><User size={12} /> Display Name</label>
                <input type="text" value={modalData.display_name} onChange={e => setModalData({...modalData, display_name: e.target.value})} required data-testid="new-staff-name" />
              </div>
              <div className="um-form-group">
                <label><Mail size={12} /> Email</label>
                <input type="email" value={modalData.email} onChange={e => setModalData({...modalData, email: e.target.value})} required data-testid="new-staff-email" />
              </div>
              <div className="um-form-group">
                <label><Lock size={12} /> Password</label>
                <input type="password" value={modalData.password} onChange={e => setModalData({...modalData, password: e.target.value})} required minLength={6} data-testid="new-staff-password" />
              </div>
              <div className="um-form-group">
                <label><Shield size={12} /> Role</label>
                <select value={modalData.role} onChange={e => setModalData({...modalData, role: e.target.value})} data-testid="new-staff-role">
                  <option value="staff">Staff</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              <div className="um-modal-actions">
                <button type="button" onClick={() => setShowModal(null)}>Cancel</button>
                <button type="submit" className="um-add-btn" disabled={actionLoading} data-testid="submit-create-staff">
                  {actionLoading ? <Loader2 size={16} className="spinner" /> : <><Plus size={16} /> Create User</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModal === 'reset-pw' && (
        <div className="um-modal-overlay" onClick={() => setShowModal(null)}>
          <div className="um-modal" onClick={e => e.stopPropagation()}>
            <div className="um-modal-header"><h2><Lock size={18} /> Reset Password</h2><button onClick={() => setShowModal(null)}><X size={18} /></button></div>
            <p style={{ color: 'var(--silver)', fontSize: '0.82rem', marginBottom: 16 }}>Set a new password for {modalData.userName}</p>
            <div className="um-form-group">
              <label>New Password</label>
              <input type="password" value={modalData.new_password} onChange={e => setModalData({...modalData, new_password: e.target.value})} minLength={6} data-testid="reset-password-input" />
            </div>
            <div className="um-modal-actions">
              <button type="button" onClick={() => setShowModal(null)}>Cancel</button>
              <button className="um-add-btn" onClick={() => handleResetPassword(modalData.userId)} disabled={actionLoading || !modalData.new_password} data-testid="submit-reset-pw">
                {actionLoading ? <Loader2 size={16} className="spinner" /> : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
